// apps/web/src/pages/estoque/localizacoes-page.tsx — Fase 13, Plano 09 (EST-07, EST-08, D-21).
// Tela "Localizações": as 5 fixas no topo (protegidas, sem ações) e as cadastradas por nome. Sem paginação (poucas linhas).
// Ações ("Nova localização", Editar, "Inativar localização") só aparecem com estoque:configurar; a autorização real é do servidor.
import { useCallback, useEffect, useState } from 'react';
import { Lock, MoreHorizontal, Plus } from 'lucide-react';
import type { LocalizacaoView } from '@pieltec/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { FormMessage } from '../../components/ui/form-message';
import { Skeleton } from '../../components/ui/skeleton';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  APOIO_SO_FIXAS,
  PERMISSAO_CONFIGURAR_ESTOQUE,
  acoesDaLocalizacao,
  contagemLocalizacoes,
  mensagemErroLocalizacao,
  ordenarLocalizacoes,
  somenteFixas,
} from '../../lib/estoque/localizacoes';
import { obterSessaoAtual } from '../../lib/session';
import { lerErroApi } from '../../lib/vendas/erro-api';
import { LocalizacaoSheet } from './localizacao-sheet';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

export function LocalizacoesPage() {
  const [lista, setLista] = useState<readonly LocalizacaoView[] | null>(null);
  const [erro, setErro] = useState(false);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [sheetAberto, setSheetAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<LocalizacaoView | null>(null);
  const [aInativar, setAInativar] = useState<LocalizacaoView | null>(null);
  const [inativando, setInativando] = useState(false);
  const [erroInativar, setErroInativar] = useState<string | null>(null);

  const carregar = useCallback(async (sinal?: { vivo: boolean }) => {
    setErro(false);
    try {
      const res = await fetch('/api/v1/estoque/localizacoes', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as LocalizacaoView[];
      if (!sinal || sinal.vivo) setLista(ordenarLocalizacoes(json));
    } catch {
      if (!sinal || sinal.vivo) setErro(true);
    }
  }, []);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    void obterSessaoAtual().then((sessao) => {
      if (sinal.vivo) setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  const podeConfigurar = permissoes.includes(PERMISSAO_CONFIGURAR_ESTOQUE);

  function abrirNova(): void {
    setEmEdicao(null);
    setSheetAberto(true);
  }

  function abrirEdicao(local: LocalizacaoView): void {
    setEmEdicao(local);
    setSheetAberto(true);
  }

  function pedirInativacao(local: LocalizacaoView): void {
    setErroInativar(null);
    setAInativar(local);
  }

  async function confirmarInativacao(): Promise<void> {
    if (!aInativar) return;
    setInativando(true);
    setErroInativar(null);
    try {
      const res = await apiMutate(`/api/v1/estoque/localizacoes/${aInativar.id}/inativar`, {});
      if (!res.ok) {
        const falha = await lerErroApi(res, 'Não foi possível inativar a localização.');
        setErroInativar(mensagemErroLocalizacao(falha.codigo) ?? falha.mensagem);
        return;
      }
      setAInativar(null);
      await carregar();
    } catch {
      setErroInativar('Não foi possível inativar a localização. Verifique a conexão e tente novamente.');
    } finally {
      setInativando(false);
    }
  }

  const comAcoes = podeConfigurar && (lista?.some((local) => acoesDaLocalizacao(local, permissoes).length > 0) ?? false);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Localizações</h1>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {lista ? contagemLocalizacoes(lista.length) : ' '}
            </p>
          </div>
          {podeConfigurar && (
            <Button type="button" onClick={abrirNova}>
              <Plus aria-hidden="true" />
              Nova localização
            </Button>
          )}
        </CardContent>
      </Card>

      {lista === null && !erro && (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando localizações">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      )}

      {lista && <p className="text-sm text-muted-foreground">Localizações fixas são usadas automaticamente pelos movimentos e preservam o histórico do estoque.</p>}
      {lista && (
        <Card className="gap-0 overflow-hidden py-0">
          <table className="w-full text-sm">
            <caption className="sr-only">Localizações de estoque</caption>
            <thead className="bg-slate-100 text-left">
              <tr>
                <th scope="col" className={CABECALHO}>Nome</th>
                <th scope="col" className={CABECALHO}>Tipo</th>
                <th scope="col" className={CABECALHO}>Situação</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Itens com saldo</th>
                {comAcoes && (
                  <th scope="col" className="w-12 px-1 py-2">
                    <span className="sr-only">Ações</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {lista.map((local) => {
                const acoes = acoesDaLocalizacao(local, permissoes);
                return (
                  <tr key={local.id} className="border-t">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {local.fixa && <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-label="Localização fixa" />}
                        <span className="font-semibold">{local.nome}</span>
                      </div>
                      {local.fixa ? (
                        <span className="text-xs text-muted-foreground">Localização automática</span>
                      ) : (
                        local.descricao && <p className="text-xs text-muted-foreground">{local.descricao}</p>
                      )}
                    </td>
                    <td className="px-3 py-2">{local.fixa ? 'Fixa' : 'Cadastrada'}</td>
                    <td className="px-3 py-2">{local.ativa ? 'Ativa' : 'Inativa'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{local.itensComSaldo}</td>
                    {comAcoes && (
                      <td className="w-12 px-1 py-1 text-right">
                        {acoes.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button type="button" variant="ghost" size="icon" aria-label={`Mais ações da localização ${local.nome}`} className="size-11 sm:size-9">
                                <MoreHorizontal aria-hidden="true" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {acoes.includes('editar') && (
                                <DropdownMenuItem className="min-h-10" onSelect={() => abrirEdicao(local)}>
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {acoes.includes('inativar') && (
                                <DropdownMenuItem className="min-h-10" onSelect={() => pedirInativacao(local)}>
                                  Inativar localização
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {lista && somenteFixas(lista) && <p className="text-sm text-muted-foreground">{APOIO_SO_FIXAS}</p>}

      {erro && (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar as localizações. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      )}

      <LocalizacaoSheet
        aberto={sheetAberto}
        onAbertoChange={setSheetAberto}
        localizacao={emEdicao}
        onSalvo={() => void carregar()}
        onConflito={() => void carregar()}
      />

      <AlertDialog open={aInativar !== null} onOpenChange={(aberto) => !aberto && !inativando && setAInativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar localização</AlertDialogTitle>
            <AlertDialogDescription>
              {aInativar ? `A localização "${aInativar.nome}" deixa de aparecer nas movimentações novas. O histórico é mantido.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {erroInativar && (
            <div role="alert">
              <FormMessage>{erroInativar}</FormMessage>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inativando}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={inativando}
              onClick={(event) => {
                event.preventDefault();
                void confirmarInativacao();
              }}
            >
              {inativando ? 'Inativando...' : 'Inativar localização'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
